import { motion, useReducedMotion } from "framer-motion";

/**
 * MaskedLines — line-by-line reveal for a hero headline.
 * Uses fade + translate (no overflow-hidden mask) so descenders and
 * ascenders can never be clipped mid-animation.
 * Respects prefers-reduced-motion.
 */
export function MaskedLines({ lines, delay = 0.1, className = "", as: Tag = "h1", stagger = 0.12 }) {
  const reduce = useReducedMotion();
  return (
    <Tag className={className} aria-label={typeof lines[0] === "string" ? lines.join(" ") : undefined}>
      {lines.map((line, i) => (
        <motion.span
          key={i}
          className="line-mask"
          initial={reduce ? { opacity: 1, y: 0, filter: "blur(0px)" } : { y: 40, opacity: 0, filter: "blur(6px)" }}
          animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
          transition={
            reduce
              ? { duration: 0 }
              : { duration: 1.0, ease: [0.2, 0.7, 0.2, 1], delay: delay + i * stagger }
          }
          style={{ display: "block", willChange: reduce ? "auto" : "transform, opacity, filter" }}
        >
          {line}
        </motion.span>
      ))}
    </Tag>
  );
}

/** RiseIn — generic scroll-triggered rise-and-fade for sections. */
export function RiseIn({ children, delay = 0, y = 32, once = true, className = "", style }) {
  const reduce = useReducedMotion();
  if (reduce) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.25 }}
      transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1], delay }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/** Reveal a single word/line with a mask (scroll-triggered). */
export function MaskedLineOnScroll({ children, delay = 0, className = "", as: Tag = "h2" }) {
  const reduce = useReducedMotion();
  if (reduce) {
    return <Tag className={className}><span className="line-mask">{children}</span></Tag>;
  }
  return (
    <Tag className={className}>
      <span className="line-mask">
        <motion.span
          initial={{ y: "100%" }}
          whileInView={{ y: "0%" }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1], delay }}
          style={{ display: "inline-block" }}
        >
          {children}
        </motion.span>
      </span>
    </Tag>
  );
}
